import React, { useMemo } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Paper, Box, Typography } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';

// --- Funzione Helper per formattare i minuti in HH:mm ---
const formatMinutesToHHMM = (totalMinutes) => {
    if (isNaN(totalMinutes) || totalMinutes === 0) return "00:00";
    const h = Math.floor(totalMinutes / 60);
    const m = Math.round(totalMinutes % 60); 
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

// --- FUNZIONE DI CALCOLO OTTIMIZZATA ---
const buildOptimizedRows = (inizio, fine, data) => {
    // Se mancano le date o i dati, restituisci array vuoto
    if (!inizio || !fine || !data) return [];

    // 1. Pre-calcoliamo le statistiche giornaliere
    const dailyStatsMap = new Map();

    data.forEach(action => {
        if (!action.orario_inizio || !action.orario_fine) return;

        const dateStr = dayjs(action.data).format('YYYY-MM-DD');
        const start = dayjs(`1970-01-01T${action.orario_inizio}`);
        const end = dayjs(`1970-01-01T${action.orario_fine}`);

        if (start.isValid() && end.isValid() && end.isAfter(start)) {
            const durationMinutes = end.diff(start, 'minute');

            if (!dailyStatsMap.has(dateStr)) {
                dailyStatsMap.set(dateStr, { totalMinutes: 0, count: 0 });
            }

            const stats = dailyStatsMap.get(dateStr);
            stats.totalMinutes += durationMinutes;
            stats.count += 1;
        }
    });

    // 2. Costruiamo le righe iterando dal giorno INIZIO al giorno FINE
    const rows = [];
    let currentDay = dayjs(inizio);
    const endDate = dayjs(fine);

    // Protezione per loop infinito se le date sono invalide
    if (!currentDay.isValid() || !endDate.isValid()) return [];

    let idCounter = 0;
    while (currentDay.isBefore(endDate) || currentDay.isSame(endDate, 'day')) {
        const dateStr = currentDay.format('YYYY-MM-DD');
        const stats = dailyStatsMap.get(dateStr) || { totalMinutes: 0, count: 0 };

        rows.push({
            id: idCounter++,
            giorno: currentDay.toDate(), // Oggetto Date per l'ordinamento corretto della DataGrid
            tot_ore: formatMinutesToHHMM(stats.totalMinutes),
            media_ore: formatMinutesToHHMM(stats.count > 0 ? stats.totalMinutes / stats.count : 0),
            // Dati grezzi per il totale
            _rawMinutes: stats.totalMinutes,
            _rawCount: stats.count
        });

        currentDay = currentDay.add(1, 'day');
    }

    return rows;
};

export default function ReportTableAvgTime({ data, startDate, endDate }) {
    
    const columns = [
        { field: 'giorno', headerName: 'Giorno', width: 150, type: "date" },
        { 
            field: 'tot_ore', headerName: 'Totale Ore', width: 130, align: 'center', headerAlign: 'center',
            cellClassName: (params) => clsx('super-app', { total: params.row.id === "totale" })
        },
        { 
            field: 'media_ore', headerName: 'Media Ore', width: 130, align: 'center', headerAlign: 'center',
            cellClassName: (params) => clsx('super-app', { total: params.row.id === "totale" })
        },
    ];

    // NOTA: Qui mappiamo le props corrette (startDate, endDate, data)
    const rows = useMemo(() => 
        buildOptimizedRows(startDate, endDate, data), 
        [startDate, endDate, data]
    );

    // Calcolo della riga dei totali
    const totalRow = useMemo(() => {
        if (rows.length === 0) return null;
        
        const totalMinutes = rows.reduce((sum, row) => sum + row._rawMinutes, 0);
        const totalCount = rows.reduce((sum, row) => sum + row._rawCount, 0);

        return {
            id: 'totale',
            giorno: null, // Per evitare errori di formattazione data sulla riga totale, o gestiscilo nel valueFormatter
            tot_ore: formatMinutesToHHMM(totalMinutes),
            media_ore: formatMinutesToHHMM(totalCount > 0 ? totalMinutes / totalCount : 0),
        };
    }, [rows]);

    // Uniamo le righe normali con la riga del totale (se esiste)
    const finalRows = useMemo(() => {
        if (!totalRow) return [];
        // Creiamo una riga speciale per visualizzare "TOTALE" nella colonna data
        const totalRowFormatted = { 
            ...totalRow, 
            giorno: new Date(8640000000000000) // Hack per metterlo in fondo o gestirlo via footer
        };
        return [...rows, { ...totalRow, id: 'totale' }]; 
    }, [rows, totalRow]);

    return (
        <Box 
            sx={{ 
                height: 600, 
                width: '100%',
                '& .super-app.total': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    fontWeight: '600',
                }
            }}
        >
            <DataGrid
                rows={finalRows}
                columns={columns}
                getRowId={(row) => row.id}
                // Customizziamo la cella "Giorno" per la riga totale
                getCellClassName={(params) => {
                    if (params.id === 'totale') return 'super-app total';
                    return '';
                }}
                slots={{
                    toolbar: GridToolbar,
                    noRowsOverlay: () => (
                        <Box p={2} display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography>Seleziona un intervallo di date per vedere i dati.</Typography>
                        </Box>
                    )
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                    },
                }}
            />
        </Box>
    );
}